import * as React from 'react';

import { AlertTriangleIcon } from '../../icons';
import { cx } from '../../utils/cx';

type AvatarSize = '3xs' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

export type AvatarShape = 'circle' | 'square';

export interface IAvatarProps {
  size?: AvatarSize;
  shape?: AvatarShape;
  className?: string;
  src?: string | null;
  alt?: string;
  /**
   * Display a contrast border around the avatar.
   */
  contrastBorder?: boolean;

  /**
   * The initials of the user to display if no image is available.
   */
  initials?: string;

  /**
   * Whether the avatar should show a focus ring when the parent group is in focus.
   * For example, when the avatar is wrapped inside a link.
   *
   * @default false
   */
  focusable?: boolean;
}

const sizeStyles = {
  '3xs': {
    root: 'size-5 outline-[0.75px] -outline-offset-1',
    initials: 'text-2xs font-semibold',
    icon: 'size-2.5',
    squareRadius: 'rounded-[4px]',
  },
  '2xs': {
    root: 'size-6 outline-[0.75px] -outline-offset-1',
    initials: 'text-xs font-semibold',
    icon: 'size-3',
    squareRadius: 'rounded-[4px]',
  },
  xs: {
    root: 'size-8 outline-[0.75px] -outline-offset-1',
    initials: 'text-xs font-semibold',
    icon: 'size-4',
    squareRadius: 'rounded-[4px]',
  },
  sm: {
    root: 'size-9 outline-[0.75px] -outline-offset-1',
    initials: 'text-sm font-semibold',
    icon: 'size-5',
    squareRadius: 'rounded-[6px]',
  },
  md: {
    root: 'size-10 outline-[0.75px] -outline-offset-1',
    initials: 'text-md font-semibold',
    icon: 'size-6',
    squareRadius: 'rounded-[8px]',
  },
  lg: {
    root: 'size-12 outline-[0.75px] -outline-offset-1',
    initials: 'text-lg font-semibold',
    icon: 'size-7',
    squareRadius: 'rounded-[8px]',
  },
  xl: {
    root: 'size-14 outline-[0.75px] -outline-offset-1',
    initials: 'text-xl font-semibold',
    icon: 'size-8',
    squareRadius: 'rounded-[8px]',
  },
  '2xl': {
    root: 'size-16 outline-[0.75px] -outline-offset-1',
    initials: 'text-display-xs font-semibold',
    icon: 'size-8',
    squareRadius: 'rounded-[10px]',
  },
  '3xl': {
    root: 'size-18 outline-[0.75px] -outline-offset-1',
    initials: 'text-display-sm font-semibold',
    icon: 'size-9',
    squareRadius: 'rounded-[10px]',
  },
  '4xl': {
    root: 'size-28 outline-[0.75px] -outline-offset-1',
    initials: 'text-display-md font-semibold',
    icon: 'size-12',
    squareRadius: 'rounded-2xl',
  },
};

const shapeStyles = {
  circle: 'rounded-full',
};

const Avatar = ({
  contrastBorder = true,
  size = 'md',
  shape = 'circle',
  src,
  alt,
  initials,
  focusable = false,
  className,
}: IAvatarProps) => {
  // Keyed on the URL that failed rather than a bare flag: an avatar outlives
  // the row it draws, so a corrected `src` on a mounted avatar has to replace
  // the fallback the previous one left behind.
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null);
  const borderRadiusClass = shape === 'circle' ? shapeStyles.circle : sizeStyles[size].squareRadius;

  const renderMainContent = () => {
    if (src && src !== failedSrc) {
      return (
        <img
          data-avatar-img
          className={cx('size-full object-cover', borderRadiusClass)}
          src={src}
          alt={alt}
          onError={() => setFailedSrc(src)}
        />
      );
    }

    if (initials) {
      return <span className={cx('text-quaternary', sizeStyles[size].initials)}>{initials}</span>;
    }

    return <AlertTriangleIcon className={cx('text-fg-quaternary', sizeStyles[size].icon)} />;
  };

  return (
    <div
      data-avatar
      className={cx(
        'bg-tertiary relative inline-flex shrink-0 items-center justify-center outline-transparent',
        // Focus styles
        focusable && 'group-outline-focus-ring group-focus-visible:outline-2 group-focus-visible:outline-offset-2',
        contrastBorder && 'outline-secondary_alt outline',
        sizeStyles[size].root,
        borderRadiusClass,
        className
      )}
    >
      {renderMainContent()}
    </div>
  );
};

export default Avatar;
